export default {
  navigation: {
    section: 'Billing',
    billing: 'Billing',
    usage: 'Consumption',
  },
  page: {
    title: 'Billing',
    subtitle: 'Your balance, what you consume and your invoices. Amounts exclude VAT.',
    subtitleVolumes: 'What your content consumes, day by day.',
    balance: 'Balance {amount}',
    tabs: {
      overview: 'Overview',
      usage: 'Consumption',
      history: 'History',
      preferences: 'Preferences',
    },
  },

  overview: {
    noWallet: 'This platform grants no credit: no wallet is open for this organization.',
    viewPricing: 'See the full pricing',
    pending: {
      message: 'A top-up of {amount} is waiting for your payment.',
      detail: 'It expires on {date}. Nothing has been charged.',
      pay: 'Pay',
      cancel: 'Cancel',
    },
    collecting: {
      message: 'The payment of your {amount} top-up is in progress.',
      detail:
        'Your credits will be added automatically once your bank has confirmed the debit, which takes 8 days on average.',
    },
    balance: {
      label: 'Available balance',
      runway: '≈ {days} days',
      runwayHint: 'At {rate} a day',
      bought: '{amount} bought, never lapses',
      granted: '{amount} granted, until {date}',
      grantedNoDate: '{amount} granted',
      incoming: '+ {amount} pending: a debit is in progress, and the credits will be added once it is confirmed',
      nextGrant: 'Next grant of {amount} on {date}',
      autoTopUp: 'Automatic top-up',
      autoTopUpOn: 'on',
      autoTopUpOff: 'off',
      configure: 'Adjust',
      activate: 'Switch on',
    },
    month: {
      spent: 'Spent in {month}',
      projection: 'Estimated month end',
      chart: 'Spending per day in {month}',
    },
    breakdown: {
      title: 'Where your credit goes this month',
      details: 'Detailed consumption',
      empty: 'Nothing consumed this month.',
      included: 'Included',
      categories: {
        subscriptions: 'Channels and domains',
        storage: 'Storage',
        subtitles: 'Subtitles',
        delivery: 'Delivery',
        other: 'Other',
      },
      subscriptionsCount: '{count} subscription | {count} subscriptions',
      storageVolume: '{duration} of video · {size} in all',
      subtitlesVolume: 'Transcriptions and translations',
      deliveryVolume: '{used} of {included} included',
    },
    subscriptions: {
      title: 'Subscriptions',
      next: 'next charge on {date}',
    },
    recent: {
      title: 'Latest movements',
      all: 'Full history',
      empty: 'No movement yet.',
    },
  },

  credits: {
    deletionNotice:
      'Your wallet has been empty for a while: your hosted content may be deleted from {date}. Top up to avoid it.',
    allocations: {
      sources: {
        purchase: 'Top-up',
        grant: 'Free grant',
        bonus: 'Top-up bonus',
        auto_renewal: 'Automatic top-up',
        refund: 'Refund',
        adjustment: 'Adjustment',
      },
    },
  },
  recurring: {
    kinds: {
      channel: 'Channel',
      domain: 'Custom domain',
    },
    status: {
      active: 'Active',
      past_due: 'Past due',
      suspended: 'Suspended',
      cancelled: 'Cancelled',
    },
    graceEnds: 'Taken offline on {date}',
  },
  preferences: {
    identity: {
      title: 'Billing identity',
      lede: 'What your invoices are made out to.',
      edit: 'Edit',
      fill: 'Fill in',
      empty: 'No identity on file: you will be asked for it at your first top-up.',
      unavailable: 'The invoicing service is not answering: the billing identity cannot be shown right now.',
      vat: 'EU VAT number',
      vatValid: 'checked',
      vatInvalid: 'unknown to VIES',
    },
    explainer: {
      title: 'How the wallet works',
      facts: '1 credit = €1 excl. VAT · free credit every month · a bonus on top-ups',
    },
  },
  history: {
    views: {
      label: 'Show',
      movements: 'Movements',
      documents: 'Invoices and credit notes',
    },
    filters: {
      label: 'Kind of movement',
      all: 'All',
      topUps: 'Top-ups',
      consumption: 'Consumption',
      grants: 'Granted',
      refunds: 'Refunds',
      expirations: 'Expirations',
    },
    export: 'Export as CSV',
    empty: 'No movement yet.',
    emptyFiltered: 'No movement of this kind.',
    loadMore: 'Show more',
    folded: '{from} – {to} · {count} day | {from} – {to} · {count} days',
    showDays: 'Show day by day',
    hideDays: 'Hide the days',
    balanceAfter: 'Balance after: {amount}',
    topUp: {
      title: 'Top-up',
      auto: 'Automatic top-up',
      paid: 'Paid',
      withBonus: '{amount} + {bonus} bonus',
      invoice: 'Download the invoice',
      detail: 'See the detail',
      expiresOn: 'expires on {date}',
      collecting: 'credits added once your bank has confirmed the debit',
    },
    csv: {
      filename: 'movements',
      date: 'Date',
      type: 'Type',
      description: 'Description',
      amount: 'Amount (€ excl. VAT)',
      balance: 'Balance after (€)',
    },
  },
  creditHistory: {
    types: {
      purchase: 'Top-up',
      bonus: 'Bonus',
      grant: 'Free grant',
      auto_renewal: 'Automatic top-up',
      consumption: 'Consumption',
      refund: 'Refund',
      adjustment: 'Adjustment',
      expiration: 'Expiry',
    },
    descriptions: {
      freeCredit: 'Free credit of the month',
      bonus: 'Bonus on the top-up',
      topUp: 'Credits bought',
      autoTopUp: 'Credits bought by the automatic top-up',
      refund: 'Credits taken back by a refund',
      expired: 'Unused free credit, expired',
      skus: {
        storage_daily: 'File storage',
        storage_video_daily: 'Video storage',
        // The first month of a file's storage, debited when it lands, a video's when it is measured.
        storage_first_month: 'First month of storage',
        storage_video_first_month: 'First month of video storage',
        bandwidth_delivered: 'Delivery beyond the allowance',
        subtitles_transcription: 'Automatic transcription',
        subtitles_translation: 'Automatic translation',
        websites_channel_monthly: 'Channel, monthly fee',
        websites_domain_monthly: 'Custom domain, monthly fee',
      },
    },
  },
  purchases: {
    pay: 'Pay',
    reconcile: 'Check with the invoicing service',
    cancel: 'Cancel',
    cancelConfirm: {
      title: 'Cancel this top-up?',
      message:
        'The {amount} top-up will be closed and can no longer be paid. Nothing was charged and nothing will be.',
      confirm: 'Cancel the top-up',
      keep: 'Keep it',
    },
    cancelled: 'Top-up cancelled. Nothing was charged.',
    cancelRefused:
      'This top-up can no longer be cancelled: it was paid meanwhile, and its credits are in the wallet.',
    reconciled: {
      paid: 'Payment confirmed: the credits are in the wallet.',
      pending: 'The payment is still in progress at the invoicing service ({status}). Try again later.',
      closed: 'This top-up was closed without payment ({status}). Nothing was charged.',
      not_opened:
        'This top-up never reached the invoicing service: there is nothing to check and nothing was charged.',
    },
  },
  documents: {
    empty: 'No invoice yet.',
    download: 'Download the PDF',
    pdfPending: 'PDF being rendered',
    notReady: 'This document is issued but its PDF is not ready yet. Try again in a moment.',
    settled: 'settled: {amount}',
    filters: {
      kind: 'Document type',
      allKinds: 'All types',
    },
    kinds: {
      invoice: 'Invoice',
      credit_note: 'Credit note',
    },
    table: {
      number: 'Number',
      kind: 'Type',
      issuedOn: 'Issued on',
      exclTax: 'Excl. tax',
      inclTax: 'Incl. tax',
    },
  },
  identity: {
    kind: 'You are buying as',
    kindFrozen: 'You are buying as a {kind}. This choice can no longer be changed.',
    kinds: {
      company: 'company',
      individual: 'individual',
    },
    kindOptions: {
      company: 'Company',
      individual: 'Individual',
    },
    contactName: 'Contact name',
    fullName: 'Full name',
    companyName: 'Registered company name',
    email: 'Billing email',
    country: 'Country',
    line1: 'Address',
    line2: 'Address line 2',
    postalCode: 'Postal code',
    city: 'City',
    vatNumber: 'EU VAT number (optional)',
    vat: {
      valid: 'VAT number verified',
      invalid: 'Unknown to VIES: correct it or clear the field.',
      unknown: 'VAT number not verified yet: the European register (VIES) is not answering for now.',
    },
    saved: 'Billing identity saved.',
  },
  common: {
    perMonth: '/month',
  },
}
